import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from "@tanstack/react-query";
import type {
  CategoriaServicio,
  ServicioCreate,
  ServicioUpdate,
} from "@uirtus/shared";
import { api } from "./api";

export interface Servicio {
  id: string;
  clave: string;
  nombre: string;
  descripcion: string;
  categoria: CategoriaServicio;
  claveProdServ: string;
  claveUnidad: string;
  unidad: string | null;
  precioBase: string | null;
  moneda: string;
  objetoImp: string;
  tasaIva: string | null;
  tasaRetIva: string | null;
  tasaRetIsr: string | null;
  esRecurrente: boolean;
  activo: boolean;
  notas: string | null;
}

export interface ServicioConUso extends Servicio {
  uso: { cotizaciones: number; facturas: number };
}

const CLAVE = ["servicios"] as const;

export interface FiltrosServicios {
  q?: string;
  categoria?: CategoriaServicio;
  activo?: boolean;
}

export function useServicios(filtros: FiltrosServicios) {
  const parametros = new URLSearchParams();
  if (filtros.q) parametros.set("q", filtros.q);
  if (filtros.categoria) parametros.set("categoria", filtros.categoria);
  if (filtros.activo !== undefined) parametros.set("activo", String(filtros.activo));
  const cadena = parametros.toString();

  return useQuery({
    queryKey: [...CLAVE, filtros],
    queryFn: () => api<Servicio[]>(`/servicios${cadena ? `?${cadena}` : ""}`),
  });
}

function useMutacionServicio<TDatos, TVars>(
  fn: (vars: TVars) => Promise<TDatos>,
  opciones?: UseMutationOptions<TDatos, Error, TVars>,
) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: fn,
    ...opciones,
    onSuccess: (...args) => {
      void cliente.invalidateQueries({ queryKey: CLAVE });
      opciones?.onSuccess?.(...args);
    },
  });
}

export const useCrearServicio = () =>
  useMutacionServicio((body: ServicioCreate) =>
    api<Servicio>("/servicios", { method: "POST", body }),
  );

export const useActualizarServicio = () =>
  useMutacionServicio(({ id, ...body }: ServicioUpdate & { id: string }) =>
    api<Servicio>(`/servicios/${id}`, { method: "PATCH", body }),
  );

export const useCambiarActivoServicio = () =>
  useMutacionServicio(({ id, activo }: { id: string; activo: boolean }) =>
    api<Servicio>(`/servicios/${id}/${activo ? "reactivar" : "desactivar"}`, {
      method: "POST",
    }),
  );

export const useEliminarServicio = () =>
  useMutacionServicio((id: string) =>
    api<void>(`/servicios/${id}`, { method: "DELETE" }),
  );
